import React, { FC } from "react";
import CustomModal, { ModalSize } from "./CustomModal";
import { Form } from "../../utils/FormTemplates";
import EnrollmentAssistantSchema from '../Enrollment/EnrollmentAssistantSchema';
import { Package } from "../../types/api/enrollmentPackages/Package";
import { FaDownload, FaBan } from 'react-icons/fa';
import { Nullable } from "../../types/custom/Nullable";
import { EnrollmentAgentType } from "../../types/enums/EnrollmentAgentType";
import { UserDetail } from "../../types/api/users/UserDetail";
import { UserId } from "../../types/api/primaryKeys/PrimaryKey";
import { permissionsService } from "../../utils/PermissionService";
import { UserRole } from "../../types/enums/UserRole";

interface Props {
    showModal: boolean;
    activePackage: Package;
    updateEnrollmentAssistant: (e: EnrollmentAssistantFormData) => void;
    overrideEnrollmentAssistant: boolean;
    setOverride: (e: React.ChangeEvent<HTMLInputElement>) => void;
    closeEAModal: () => void;
    closeOverride: () => void;
    user: UserDetail;
    employeeInfo: EmployeeInfo;
}

interface EmployeeInfo {
    city: Nullable<string>;
    state: Nullable<string>;
}

interface Property {
    type: string,
    title: string,
    enum?: string[] | number[],
    enumNames?: string[],
    default?: string | number,
    readOnly?: boolean
}

enum PackageType {
    Harmony = 'Harmony',
    Default = 'Default'
}

enum AgentType {
    Agent = 'Agent',
    CallCenter = 'Call Center'
}

interface Schema {
    title: string;
    type: string;
    required: string[];
    dependencies?: { [key: string]: any };
    properties: { [key: string]: Property };
}

interface SchemaData {
    formData: EnrollmentAssistantFormData;
}

export interface EnrollmentAssistantFormData {
    enrollmentAssistantId: Nullable<string>;
    enrollmentAssistantName: Nullable<string>;
    enrollmentAssistantUserId: UserId;
    residentCity?: Nullable<string>;
    residentState?: Nullable<string>;
    agentType?: Nullable<EnrollmentAgentType>;
}

const stateEnum = ['AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY'];
const stateEnumNames = ['Alabama', 'Alaska', 'American Samoa', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District Of Columbia', 'Federated States Of Micronesia', 'Florida', 'Georgia', 'Guam', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Marshall Islands', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Northern Mariana Islands', 'Ohio', 'Oklahoma', 'Oregon', 'Palau', 'Pennsylvania', 'Puerto Rico', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virgin Islands', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];

export const EnrollmentAssistantModal: FC<Props> =
    ({
        showModal,
        activePackage,
        updateEnrollmentAssistant,
        overrideEnrollmentAssistant,
        setOverride,
        closeEAModal,
        closeOverride,
        user,
        employeeInfo
    }) => {
        //default schema without harmony

        const renderedSchema: Schema = EnrollmentAssistantSchema;
        //determine if the user has harmony
        let packageType: PackageType;
        //determine if the user's EA is agent or call center if applicable (harmony plan types)
        let agentType: Nullable<EnrollmentAgentType> = null;

        const formData: EnrollmentAssistantFormData =
        {
            enrollmentAssistantId: activePackage.enrollmentAssistantId,
            enrollmentAssistantName: activePackage.enrollmentAssistantName,
            enrollmentAssistantUserId: user.userId
        };

        const determineHarmonySchema = () => {
            formData.residentCity = activePackage.residentCity ?? employeeInfo.city;
            formData.residentState = activePackage.residentState ?? employeeInfo.state;
            formData.agentType = activePackage.enrollmentTypeId;
            agentType = user.enrollmentTypeId;
            renderedSchema.properties.agentType = {
                type: "number",
                title: "Agent Type",
                enum: [EnrollmentAgentType.Agent, EnrollmentAgentType.CallCenter],
                enumNames: [AgentType.Agent, AgentType.CallCenter]
            };
            renderedSchema.required.push('agentType');
            if (agentType) {
                renderedSchema.properties.agentType.readOnly = true;
                formData.agentType = agentType;
                if (agentType === EnrollmentAgentType.Agent) {
                    renderedSchema.properties.residentCity = {
                        type: "string",
                        title: "Residence City"
                    };
                    renderedSchema.properties.residentState = {
                        type: "string",
                        title: "Residence State",
                        enum: stateEnum,
                        enumNames: stateEnumNames
                    };
                    renderedSchema.required.push('residentState');
                    renderedSchema.required.push('residentCity');
                }
            } else {
                renderedSchema.properties.agentType.default = EnrollmentAgentType.Agent;
                if (!formData.agentType) {
                    formData.agentType = EnrollmentAgentType.Agent;
                }
                renderedSchema.dependencies = {
                    "agentType": {
                        "oneOf": [
                            {
                                "properties": {
                                    "agentType": {
                                        "enum": [
                                            EnrollmentAgentType.Agent
                                        ]
                                    },
                                    "residentCity": {
                                        "type": "string",
                                        "title": "Residence City"
                                    },
                                    "residentState": {
                                        "type": "string",
                                        "title": "Residence State",
                                        "enum": stateEnum,
                                        "enumNames": stateEnumNames
                                    }
                                },
                                "required": [
                                    "residentCity",
                                    "residentState"
                                ]
                            },
                            {
                                "properties": {
                                    "agentType": {
                                        "enum": [
                                            EnrollmentAgentType.CallCenter
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                };
            }
        };

        const userIsEnrollmentAssistant = permissionsService.userHasOnlyThisRole(user, UserRole.Enrollment_Assistant);

        // Some special logic to run if the current user is an Enrollment Assistant,
        // regardless of harmony or not.
        if (userIsEnrollmentAssistant) {
            // Pre-fill the 'enrollment assistant id field and mark it as readonly
            // if it is set at the user level.
            if (user.enrollmentAssistantId) {
                formData.enrollmentAssistantId = user.enrollmentAssistantId;
                renderedSchema.properties.enrollmentAssistantId.readOnly = true;
            }

            // Set the EA name to the current user's name.
            formData.enrollmentAssistantName = `${user.firstName} ${user.lastName}`;
            renderedSchema.properties.enrollmentAssistantName.readOnly = true;
        }

        // Set up some additional harmony-specific schema updates
        if (activePackage.steps.some(step => step.harmonyProductGroup !== null)) {
            packageType = PackageType.Harmony;
            determineHarmonySchema();
        } else {
            packageType = PackageType.Default;
        }

        const handleSubmit = (formData: EnrollmentAssistantFormData) => {
            const formattedData: EnrollmentAssistantFormData = {
                enrollmentAssistantId: formData.enrollmentAssistantId,
                enrollmentAssistantName: formData.enrollmentAssistantName,
                enrollmentAssistantUserId: formData.enrollmentAssistantUserId
            };
            if (packageType === PackageType.Harmony) {
                formattedData.agentType = formData.agentType;
                if (formData.agentType === EnrollmentAgentType.Agent) {
                    formattedData.residentCity = formData.residentCity;
                    formattedData.residentState = formData.residentState;
                }
            }
            updateEnrollmentAssistant(formattedData);
        };

        return (
            <CustomModal
                isModalOpen={showModal}
                modalTitle="Enrollment Assistant"
                modalSize={ModalSize.Small}
                includeCloseIcon={false}
                __onCleanup={() => closeEAModal()}
            >
                <Form schema={renderedSchema}
                    onSubmit={(data: SchemaData) => handleSubmit(data.formData)}
                    formData={formData}
                >
                    {/* allow skipping if the user is not a enrollmentassistant */}
                    {!userIsEnrollmentAssistant &&
                        <div className="form-check mb-4" style={{ marginTop: "-1em" }}>
                            <input id="overrideAssistant" type="checkbox" className="form-check-input" checked={overrideEnrollmentAssistant} onChange={e => setOverride(e)} />
                            <label className="form-check-label" htmlFor="overrideAssistant">
                                I am enrolling without an Enrollment Assistant
                            </label>
                        </div>
                    }
                    <div className="tc">
                        {!overrideEnrollmentAssistant &&
                            <React.Fragment>
                                <button type="submit" className="btn btn-primary mr-2"><FaDownload /> Save</button>
                                {packageType !== PackageType.Harmony && <button type="button" className="btn btn-default" onClick={() => closeEAModal()}><FaBan /> Cancel</button>}
                            </React.Fragment>
                        }
                        {overrideEnrollmentAssistant && <button type="button" className="btn btn-default" onClick={() => closeOverride()}>Close</button>}
                    </div>
                </Form>
            </CustomModal>
        );
    };